import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, FormHelperText, Stack } from '@mui/material';

import { FDropzone } from '../../../../../../components/base';
import { ConfirmDialog } from '../../../../../common';
import { Themes } from '../../../../../../constants';
import {
  ASSESSMENT_METHOD_VISUAL_ACCEPT,
  ASSESSMENT_METHOD_VISUAL_FILE_NAME_PATTERN,
  ASSESSMENT_METHOD_VISUAL_MAX_SIZE_MB,
} from '../constants';
import AssessmentMethodVisualCard from './AssessmentMethodVisualCard';

import type { ITreatmentPlanV3AssessmentMethodVisual } from '../../../../../../store/treatmentPlanV3';
import type { AssessmentMethodVisualsProps } from './types';

export default function AssessmentMethodVisuals({
  methodId,
  visuals,
  disabled,
  isSaving,
  readOnly,
  onCreateVisual,
  onUpdateVisual,
  onUpdateVisualFile,
  onDeleteVisual,
}: AssessmentMethodVisualsProps) {
  const [isDropzoneOpen, setIsDropzoneOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(null);
  const [pendingDeleteVisual, setPendingDeleteVisual] = useState<
    ITreatmentPlanV3AssessmentMethodVisual | 'pending' | null
  >(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const pendingPreviewUrlRef = useRef<string | null>(null);
  const visualCountRef = useRef(visuals.length);

  const revokePendingPreviewUrl = useCallback(() => {
    if (!pendingPreviewUrlRef.current) return;
    URL.revokeObjectURL(pendingPreviewUrlRef.current);
    pendingPreviewUrlRef.current = null;
  }, []);

  useEffect(() => revokePendingPreviewUrl, [revokePendingPreviewUrl]);

  const clearPendingVisual = useCallback(() => {
    revokePendingPreviewUrl();
    setPendingFile(null);
    setPendingPreviewUrl(null);
  }, [revokePendingPreviewUrl]);

  useEffect(() => {
    if (pendingFile && visuals.length > visualCountRef.current) {
      clearPendingVisual();
    }
    visualCountRef.current = visuals.length;
  }, [clearPendingVisual, pendingFile, visuals.length]);

  const handleOpenDropzone = useCallback(() => setIsDropzoneOpen(true), []);
  const handleCloseDropzone = useCallback(() => setIsDropzoneOpen(false), []);
  const applyPendingFile = useCallback(
    (file: File) => {
      revokePendingPreviewUrl();
      const previewUrl = URL.createObjectURL(file);
      pendingPreviewUrlRef.current = previewUrl;
      setPendingFile(file);
      setPendingPreviewUrl(previewUrl);
      setIsDropzoneOpen(false);
    },
    [revokePendingPreviewUrl],
  );
  const handleSelectFiles = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (!file) return;
      applyPendingFile(file);
    },
    [applyPendingFile],
  );
  const handlePendingCreated = useCallback(() => {
    clearPendingVisual();
  }, [clearPendingVisual]);
  const handleCloseDelete = useCallback(() => setPendingDeleteVisual(null), []);
  const handleConfirmDelete = useCallback(async () => {
    if (pendingDeleteVisual == null || isDeleting) return;
    if (pendingDeleteVisual === 'pending') {
      clearPendingVisual();
      setPendingDeleteVisual(null);
      return;
    }
    if (methodId == null) return;
    setIsDeleting(true);
    const deleted = await onDeleteVisual(methodId, pendingDeleteVisual.id);
    setIsDeleting(false);
    if (!deleted) return;
    setPendingDeleteVisual(null);
  }, [clearPendingVisual, isDeleting, methodId, onDeleteVisual, pendingDeleteVisual]);

  const hasNoVisuals = visuals.length === 0 && pendingFile == null;

  return (
    <Stack gap={2}>
      {methodId == null
        ? null
        : visuals.map((visual) => (
            <AssessmentMethodVisualCard
              key={visual.id}
              disabled={disabled}
              isSaving={isSaving}
              methodId={methodId}
              onCreate={onCreateVisual}
              onCreated={handlePendingCreated}
              onRequestDelete={setPendingDeleteVisual}
              onUpdate={onUpdateVisual}
              onUpdateFile={onUpdateVisualFile}
              readOnly={readOnly}
              visual={visual}
            />
          ))}
      {methodId != null && pendingFile ? (
        <AssessmentMethodVisualCard
          disabled={disabled}
          isSaving={isSaving}
          methodId={methodId}
          onCreate={onCreateVisual}
          onCreated={handlePendingCreated}
          onReplacePendingFile={applyPendingFile}
          onRequestDelete={setPendingDeleteVisual}
          onUpdate={onUpdateVisual}
          onUpdateFile={onUpdateVisualFile}
          pendingFile={pendingFile}
          pendingPreviewUrl={pendingPreviewUrl ?? undefined}
          readOnly={readOnly}
          visual={null}
        />
      ) : null}

      {readOnly ? null : (
        <Box display="flex" justifyContent="flex-end">
          <Button
            color={hasNoVisuals && methodId != null ? 'error' : 'primary'}
            disabled={disabled || isSaving || methodId == null || pendingFile != null}
            onClick={handleOpenDropzone}
            variant="outlined"
          >
            Add visual
          </Button>
        </Box>
      )}
      {hasNoVisuals && methodId != null ? (
        <FormHelperText error>At least one visual is required</FormHelperText>
      ) : null}

      <FDropzone
        accept={ASSESSMENT_METHOD_VISUAL_ACCEPT}
        colorTheme={Themes.Primary}
        fileNameRegEx={ASSESSMENT_METHOD_VISUAL_FILE_NAME_PATTERN}
        maxSize={ASSESSMENT_METHOD_VISUAL_MAX_SIZE_MB}
        onClose={handleCloseDropzone}
        onSelect={handleSelectFiles}
        show={isDropzoneOpen}
      />

      <ConfirmDialog
        confirmLabel="Delete"
        confirmLoading={isDeleting}
        message="Are you sure you want to delete this visual?"
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        open={pendingDeleteVisual != null}
        title="Confirm visual deletion?"
      />
    </Stack>
  );
}
